import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Card } from '@diwata/ironvale'
import { DiwaAPI } from '@/api/client'
import { useUI } from '@/store/ui'

// The Briefing: the familiar reports to you. What changed, what needs you, what it
// did — the morning paper for your verse. Compose-only; links carry you to judgment.

interface BriefingData {
  signals: Record<string, { id: string; title: string; importance: string; type: string; suggested_action: string }[]>
  gates: { mission_id: string; mission_title: string; run_id: string }[]
  needs_you: { radar: number; other: number; gates: number; total: number }
  activity: { event_type: string; occurred_at: string; payload: Record<string, unknown> }[]
  generated_at: string
}

const EVENT_WORDS: Record<string, string> = {
  'signal.created': 'noticed',
  'mention.linked': 'linked',
  'entity.created': 'added',
  'relation.created': 'connected',
  'enrichment.completed': 'enriched',
}

export function Briefing() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DiwaAPI(diwaUrl), [diwaUrl])
  const { data, isLoading, isError } = useQuery({
    queryKey: ['briefing'],
    queryFn: () => api.get<BriefingData>('/briefing'),
    refetchInterval: 5 * 60_000,
  })

  const domains = Object.entries(data?.signals ?? {})
  const quiet = data && domains.length === 0 && data.needs_you.total === 0

  return (
    <div className="dw-page-narrow">
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">The Briefing</div>
          <div className="dw-page-subtitle">
            {data ? `as of ${new Date(data.generated_at).toLocaleTimeString()}` : 'assembling…'}
          </div>
        </div>
      </div>

      {isLoading && <p className="dw-empty">Reading the verse…</p>}
      {isError && <p className="dw-error">The briefing didn't assemble — try again shortly.</p>}
      {quiet && (
        <Card><div className="dw-rail-empty">
          All quiet. Nothing fresh, nothing waiting on you — the springs keep flowing and
          you'll be the first to know.
        </div></Card>
      )}

      {data && data.needs_you.total > 0 && (
        <Card style={{ marginBottom: 12 }}>
          <div className="dw-rail-section-title">◈ Needs you — {data.needs_you.total}</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {data.gates.map((g) => (
              <Link key={g.run_id} to="/missions" className="dw-queue-row" style={{ textDecoration: 'none' }}>
                <span className="dw-queue-glyph">◇</span>
                <span className="dw-queue-title">A gate is open on “{g.mission_title}”
                  <span className="dw-inbox-detail">your judgment unblocks the run</span>
                </span>
              </Link>
            ))}
            {data.needs_you.radar > 0 && (
              <Link to="/needs" className="dw-queue-row" style={{ textDecoration: 'none' }}>
                <span className="dw-queue-glyph">◎</span>
                <span className="dw-queue-title">{data.needs_you.radar} recompete{data.needs_you.radar > 1 ? 's' : ''} on the radar
                  <span className="dw-inbox-detail">contracts ending in your window</span>
                </span>
              </Link>
            )}
            {data.needs_you.other > 0 && (
              <Link to="/needs" className="dw-queue-row" style={{ textDecoration: 'none' }}>
                <span className="dw-queue-glyph">?</span>
                <span className="dw-queue-title">{data.needs_you.other} open question{data.needs_you.other > 1 ? 's' : ''}
                  <span className="dw-inbox-detail">uncertain evidence asking for judgment</span>
                </span>
              </Link>
            )}
          </div>
        </Card>
      )}

      {domains.map(([domain, sigs]) => (
        <Card key={domain} style={{ marginBottom: 12 }}>
          <div className="dw-rail-section-title">{domain} — what changed</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {sigs.map((s) => (
              <Link key={s.id} to="/signals" className="dw-queue-row" style={{ textDecoration: 'none' }}>
                <span className={`dw-queue-glyph${s.importance === 'High' ? ' dw-queue-glyph--gate' : ''}`}>●</span>
                <span className="dw-queue-title">{s.title}
                  {s.suggested_action && <span className="dw-inbox-detail">{s.suggested_action}</span>}
                </span>
              </Link>
            ))}
          </div>
        </Card>
      ))}

      {data && data.activity.length > 0 && (
        <Card>
          <div className="dw-rail-section-title">While you were away</div>
          <div className="dw-inbox-detail">
            {Object.entries(
              data.activity.reduce<Record<string, number>>((acc, e) => {
                acc[e.event_type] = (acc[e.event_type] ?? 0) + 1
                return acc
              }, {}),
            )
              .map(([type, n]) => `${EVENT_WORDS[type] ?? type} ×${n}`)
              .join(' · ')}
            {' — the familiar, in the last 24 hours.'}
          </div>
        </Card>
      )}
    </div>
  )
}
